import { useEffect, useState } from 'react'
import { useHead, useSeoMeta } from '@unhead/react'
import {Clock, ArrowUpRight} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { track } from '@/lib/analytics'

interface Article {
  _id: string
  title: string
  slug: string
  excerpt: string
  author: string
  category: string
  readTime?: number
  coverImage?: string
  tags?: string[]
  createdAt: string
  content: string
}

export default function Blog() {
  useSeoMeta({
    title: 'Math Computation Blog | CalculatorSkills',
    description: 'Articles on calculator skills, computation skills in math, and teaching calculator skills to elementary students.',
    ogImage: 'https://www.calculatorskills.com/og/blog.jpg',
    twitterCard: 'summary_large_image',
  })
  useHead({
    link: [{ rel: 'canonical', href: 'https://www.calculatorskills.com/blog' }],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Math Computation Blog',
          url: 'https://www.calculatorskills.com/blog',
          description: 'Articles on calculator skills and teaching strategies for basic math.',
        }),
      },
    ],
  })

  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [active, setActive] = useState<Article | null>(null)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const { list } = await lumi.entities.blog_articles.list({
          filter: { published: true },
          sort: { createdAt: -1 },
          limit: 50,
        })
        if (mounted) setArticles(list as unknown as Article[])
      } catch (err) {
        console.error('Failed to load articles', err)
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [])

  return (
    <div className="bg-[#FAFAF7]">
      <section className="border-b-2 border-[#0A0A0A]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-12 lg:pb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-[2px] bg-[#E63946]" />
            <p className="eyebrow">/ Reading — Vol. 01</p>
          </div>
          <h1 className="headline text-4xl sm:text-5xl lg:text-7xl mb-6">
            Math Computation<br/>
            <span className="text-[#E63946]">Blog.</span>
          </h1>
          <p className="text-lg text-[#475569] max-w-2xl leading-relaxed">
            In-depth articles on calculator skills, computation skills in math, and the pedagogy of teaching calculator skills to elementary students. Written for educators, students, and curious parents.
          </p>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[0, 1].map((i) => (
              <div key={i} className="border-2 border-[#0A0A0A]/10 p-8 animate-pulse h-[420px]" />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <p className="text-[#475569] text-lg">No articles published yet. Check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-[#0A0A0A]">
            {articles.map((a, idx) => (
              <article
                key={a._id}
                onClick={() => {
                  setActive(a)
                  track('blog_read', '/blog', { slug: a.slug })
                }}
                className={`group cursor-pointer p-8 lg:p-10 hover:bg-[#0A0A0A] hover:text-[#FAFAF7] transition-colors ${
                  idx % 2 === 0 ? 'md:border-r-2 border-[#0A0A0A]' : ''
                } ${idx >= 2 ? 'border-t-2 border-[#0A0A0A]' : ''}`}
              >
                {a.coverImage && (
                  <div className="mb-6 overflow-hidden border-2 border-[#0A0A0A] aspect-[16/10]">
                    <img
                      src={a.coverImage}
                      alt={`Cover image for article: ${a.title} — calculator skills and computation tutorials`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between mb-4 text-[11px] tracking-widest font-mono uppercase opacity-70">
                  <span>{a.category}</span>
                  <span className="flex items-center gap-1.5"><Clock size={12} />{a.readTime ?? 5} min</span>
                </div>
                <h2 className="headline text-2xl lg:text-3xl mb-3">{a.title}</h2>
                <p className="text-[#475569] group-hover:text-[#FAFAF7]/70 text-[15px] leading-relaxed mb-6 line-clamp-3">
                  {a.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span className="opacity-70">By {a.author}</span>
                  <span className="flex items-center gap-1.5 font-semibold tracking-tight">
                    Read article <ArrowUpRight size={16} />
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* ARTICLE MODAL */}
      {active && (
        <div
          className="fixed inset-0 bg-[#0A0A0A]/80 backdrop-blur-sm z-[100] overflow-y-auto"
          onClick={() => setActive(null)}
        >
          <div className="min-h-full flex items-start justify-center p-4 lg:p-10">
            <article
              onClick={(e) => e.stopPropagation()}
              className="bg-[#FAFAF7] border-2 border-[#0A0A0A] max-w-3xl w-full p-8 lg:p-12 my-8 reveal"
            >
              <div className="flex items-center justify-between mb-6 text-[11px] tracking-widest font-mono uppercase text-[#475569]">
                <span>{active.category}</span>
                <button onClick={() => setActive(null)} className="hover:text-[#E63946]">CLOSE ✕</button>
              </div>
              <h2 className="headline text-3xl lg:text-5xl mb-4">{active.title}</h2>
              <p className="text-sm text-[#475569] mb-8">By {active.author} · {active.readTime ?? 5} min read</p>
              {active.coverImage && (
                <img
                  src={active.coverImage}
                  alt={`Cover image: ${active.title}`}
                  className="w-full border-2 border-[#0A0A0A] aspect-[16/9] object-cover mb-8"
                />
              )}
              <div className="prose prose-neutral max-w-none text-[#0A0A0A] leading-relaxed text-[16px] whitespace-pre-line">
                {active.content}
              </div>
            </article>
          </div>
        </div>
      )}
    </div>
  )
}
